import React, { useEffect, useMemo, useRef } from "react";
import { InputWrapper } from "common/components/Wrapper/InputBase";
import { useValidCheckGroup } from "common/hooks/validCheck";
import { CheckboxItem } from "./CheckBoxItem";
import { useMultiSelect } from "common/hooks/useSelect";

export const CheckboxGroup = ({
  //InputWrapper
  label,
  caption,
  direction = "column",

  //CheckboxGroup
  name,
  required,
  children,
  value,
  onChange,
}) => {
  const { inputProps, error, onValueChange } = useValidCheckGroup(name, {
    required: required || false,
  });

  const values = value ?? inputProps.value ?? [];
  const fixedValues = useMemo(() => {
    return React.Children.toArray(children)
      .map((child) => child.props)
      .filter((p) => p.fixed)
      .map((p) => p.value);
  }, [children]);

  const { currentSet, setValue, toggle } = useMultiSelect({
    value: values,
    onChange: (v) => {
      onValueChange(v);
      onChange?.(v);
    },
    fixedValues,
    defaultValue: Array.isArray(values) ? values : [],
  });

  const didInitRef = useRef(false);

  useEffect(() => {
    if (value !== undefined && Array.isArray(value)) {
      // 값이 실제로 변경되었을 때만 업데이트
      if (
        JSON.stringify([...currentSet].sort()) !==
        JSON.stringify([...value].sort())
      ) {
        setValue(value);
      }
    }
  }, [value]);

  useEffect(() => {
    if (fixedValues.length > 0 && !didInitRef.current) {
      setValue(fixedValues);
      onValueChange(fixedValues);
      didInitRef.current = true;
    }
  }, [fixedValues]);

  return (
    <InputWrapper
      label={label}
      caption={caption}
      rules={{ required }}
      direction={direction}
      error={error}>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          checked: currentSet.has(child.props.value),
          onToggle: () => toggle(child.props.value),
        })
      )}
    </InputWrapper>
  );
};

CheckboxGroup.ByOptions = ({
  name,
  label,
  required,
  options,
  value,
  onChange,
  direction = "column",
  size = "medium",
  disabled = false,
}) => {
  return (
    <CheckboxGroup
      name={name}
      label={label}
      required={required}
      direction={direction}
      value={value}
      onChange={onChange}>
      {options.map((opt) => (
        <CheckboxItem
          key={opt.value}
          value={opt.value}
          label={opt.label}
          fixed={opt.fixed}
          size={size}
          disabled={disabled || opt.disabled}
        />
      ))}
    </CheckboxGroup>
  );
};
